import { computed } from 'vue';

import { GlobalState } from '@/global';
import { hasPermission } from '@/shared/helpers/permissions.helpers';

// Marketing site. Product pages live at `${MARKETING_BASE}/<slug>` and the
// contact-sales form at `${MARKETING_BASE}/contact` (see the matching prefill
// logic in the zynosuite-web repo's contact.astro).
const MARKETING_BASE = 'https://zynosuite.com';

export interface IUpsellModule {
    // App key — matches an entry in a tenant's `apps` entitlement boundary
    // (see IIdentityResponse.tenants[].apps, populated from permissionsBoundary).
    key: string;
    // Slug for both the marketing product page and the contact form's ?m= param.
    slug: string;
    // Marketing brand name.
    name: string;
    // One-line positioning, shown next to the name.
    tagline: string;
    // Short pitch shown under the name.
    description: string;
    // FontAwesome classes (mirrors the launcher icons in launcher-items.ts).
    icon: string;
    // Ownership override. Most modules are entitled at the app level (their
    // `key` is in the tenant's `apps` boundary), but some are sub-features of
    // another app (e.g. Bookings lives under Sales) with no top-level app key —
    // those decide ownership from a permission instead.
    isOwned?: () => boolean;
}

// The end-user products a tenant can buy, in pitch order. RMM (`msp`), Admin,
// SysAdmin and the universal `core` app are intentionally excluded — they are
// not purchasable add-on modules.
export const UpsellModules: IUpsellModule[] = [
    {
        key: 'sales',
        slug: 'sales',
        name: 'ZynoSales',
        tagline: 'Point of sale & commerce',
        description: 'Ring up sales, take payments, manage products and gift cards, fulfill orders, and run reports — all from one register.',
        icon: 'fad fa-dollar-sign'
    },
    {
        key: 'bookings',
        slug: 'bookings',
        name: 'ZynoBookings',
        tagline: 'Scheduling & deposits',
        description: 'Take appointments and class bookings with schedules, resources, deposits, waitlists, and cancellation rules.',
        icon: 'fad fa-calendar-check',
        // Bookings is a Sales sub-feature, not a top-level app — gate on its permission.
        isOwned: () => hasPermission('sales', 'bookingsEditAll')
    },
    {
        key: 'talk',
        slug: 'talk',
        name: 'ZynoTalk',
        tagline: 'Business phone, SMS & fax',
        description: 'A complete business phone system: calls, voicemail, texting, and fax, with recordings, transcripts, and activity reports.',
        icon: 'fad fa-phone-alt'
    },
    {
        key: 'crm',
        slug: 'crm',
        name: 'ZynoCRM',
        tagline: 'Customers & contacts',
        description: 'Store customer details, history, notes, files, and memberships in one organized record.',
        icon: 'fad fa-users'
    },
    {
        key: 'inventory',
        slug: 'inventory',
        name: 'ZynoInventory',
        tagline: 'Stock & catalog',
        description: 'Track items and stock levels with categories, vendors, barcode scanning, label printing, and CSV import.',
        icon: 'fad fa-shelves'
    },
    {
        key: 'forms',
        slug: 'forms',
        name: 'ZynoForms',
        tagline: 'Intake & waivers',
        description: 'Collect structured responses from customers, staff, and public links, then review and export every submission.',
        icon: 'fad fa-file-lines'
    },
    {
        key: 'display',
        slug: 'display',
        name: 'ZynoDisplay',
        tagline: 'Digital signage',
        description: 'Run signage and menu boards on any TV, tablet, or player, with media, playlists, scheduling, and live control.',
        icon: 'fad fa-tv'
    }
];

export interface IUpsellModuleState extends IUpsellModule {
    // Whether the current tenant already has this module in its entitlement.
    owned: boolean;
    // Marketing product page, e.g. https://zynosuite.com/crm
    url: string;
}

export interface IUpsellState {
    // Every purchasable module, each flagged with whether the tenant owns it.
    modules: IUpsellModuleState[];
    // The subset the tenant does not yet have.
    missing: IUpsellModuleState[];
    isAdmin: boolean;
    // True only when an admin has at least one module left to add — gates the
    // sidebar prompt so non-admins and fully-provisioned tenants never see it.
    show: boolean;
}

// The app keys the current tenant is entitled to (its permissions boundary).
function entitledAppKeys(): Set<string> {
    const tenantId = GlobalState.tenant.value?.id;
    const apps = GlobalState.user.value?.tenants.find(t => t.id === tenantId)?.apps;
    return new Set(apps ?? []);
}

// Whether the tenant has a module: by its own permission override when present
// (e.g. Bookings), otherwise by top-level app-key entitlement.
function isModuleOwned(m: IUpsellModule, entitled: Set<string>): boolean {
    return m.isOwned ? m.isOwned() : entitled.has(m.key);
}

// Reactive upsell state for the shell. Only tenant admins ever see the prompt,
// and only when the tenant is missing at least one purchasable module.
export function useUpsellState() {
    return computed<IUpsellState>(() => {
        const entitled = entitledAppKeys();
        const modules = UpsellModules.map<IUpsellModuleState>(m => ({
            ...m,
            owned: isModuleOwned(m, entitled),
            url: `${MARKETING_BASE}/${m.slug}`
        }));
        const missing = modules.filter(m => !m.owned);
        const isAdmin = !!GlobalState.tenant.value?.isAdmin;
        return { modules, missing, isAdmin, show: isAdmin && missing.length > 0 };
    });
}

// Contact-sales link with the admin's context pre-attached. The marketing site
// reads these params to prefill the form (name/email/business), pre-check the
// missing modules (`m`), fade the ones already owned (`owned`), and flag the
// lead as an existing tenant — then strips them from the visible URL via
// history.replaceState.
export function buildContactSalesUrl(): string {
    const params = new URLSearchParams();
    const user = GlobalState.user.value;
    const tenant = GlobalState.tenant.value;

    const name = [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim();
    if (name) params.set('name', name);
    if (user?.email) params.set('email', user.email);
    if (tenant?.name) params.set('business', tenant.name);
    if (tenant?.id) params.set('tenant', tenant.id);

    const entitled = entitledAppKeys();
    const missingSlugs = UpsellModules.filter(m => !isModuleOwned(m, entitled)).map(m => m.slug);
    const ownedSlugs = UpsellModules.filter(m => isModuleOwned(m, entitled)).map(m => m.slug);
    if (missingSlugs.length) params.set('m', missingSlugs.join(','));
    if (ownedSlugs.length) params.set('owned', ownedSlugs.join(','));
    params.set('existing', '1');

    return `${MARKETING_BASE}/contact?${params.toString()}`;
}
